import React, { useState, useCallback, useEffect } from 'react';
import { Search, Plus, Trash2, AlertCircle, Package, X, Layers, Box, Building2, Star } from 'lucide-react';
import purchasesService from '../services/purchasesService';
import inventoryService from '../services/inventoryService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import ProductFormComplete from './ProductFormComplete';

const PurchaseItemsGrid = ({
  items,
  setItems,
  errors,
  onValidationChange,
  disabled = false,
  supplierId = null,
  initialDiscount = null,
  initialDiscountMode = null
}) => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddMode, setQuickAddMode] = useState(null); // 'variant' or 'product'
  const [showProductFormComplete, setShowProductFormComplete] = useState(false);

  // Quick add variant form
  const [variantForm, setVariantForm] = useState({
    product_id: '',
    sku_variacao: '',
    cor: '',
    tamanho_label: '',
    medida_text: '',
    espessura_mm: '',
    comprimento_cm: '',
    largura_cm: '',
    preco_custo: ''
  });

  // Quick add product form
  const [productForm, setProductForm] = useState({
    nome: '',
    sku: '',
    categoria_id: '',
    marca_id: '',
    unidade_id: ''
  });

  const [selectedProduct, setSelectedProduct] = useState(null);

  // Estados de Ajuste da Nota (Desconto ou Acréscimo / Frete / Valor Final)
  const [discountMode, setDiscountMode] = useState(initialDiscountMode || 'target'); // 'target' | 'discount_val' | 'addition_val' | 'percent'
  const [discountInput, setDiscountInput] = useState(initialDiscount ? String(initialDiscount) : '');

  useEffect(() => {
    if (initialDiscount !== null && initialDiscount !== undefined && initialDiscount !== '') {
      setDiscountInput(String(initialDiscount));
      if (initialDiscountMode) {
        setDiscountMode(initialDiscountMode);
      }
    }
  }, [initialDiscount, initialDiscountMode]);

  // Queries for dropdown data
  const { data: categoriesData } = useQuery({
    queryKey: ['inventory-categories', { ativo: true }],
    queryFn: () => inventoryService.getCategories({ ativo: true, limit: 100 })
  });

  const { data: brandsData } = useQuery({
    queryKey: ['inventory-brands'],
    queryFn: inventoryService.getBrands
  });

  const { data: unitsData } = useQuery({
    queryKey: ['inventory-units'],
    queryFn: inventoryService.getUnits
  });

  const { data: productsData } = useQuery({
    queryKey: ['products-for-variant'],
    queryFn: () => inventoryService.getProducts({ ativo: true })
  });

  // Mutation for creating variant
  const createVariantMutation = useMutation({
    mutationFn: ({ productId, data }) => inventoryService.createVariant(productId, data),
    onSuccess: (response) => {
      toast.success('Variante criada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['products-for-variant'] });
      if (response?.data?.id) {
        handleSelectProduct({
          id: response.data.id,
          produto_id: response.data.produto_id,
          nome: selectedProduct?.nome || 'Novo Produto',
          sku_variacao: variantForm.sku_variacao,
          cor: variantForm.cor,
          tamanho_label: variantForm.tamanho_label,
          medida_text: variantForm.medida_text,
          preco_custo: variantForm.preco_custo
        });
      }
      resetQuickAddForms();
      setShowQuickAdd(false);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao criar variante');
    }
  });

  // Mutation for creating product
  const createProductMutation = useMutation({
    mutationFn: (data) => inventoryService.createProduct(data),
    onSuccess: (response) => {
      toast.success('Produto criado com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['products-for-variant'] });
      if (response?.data?.id) {
        const newProduct = response.data;
        setSelectedProduct(newProduct);
        setVariantForm(prev => ({ ...prev, product_id: newProduct.id.toString() }));
        setQuickAddMode('variant');
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao criar produto');
    }
  });

  const resetQuickAddForms = () => {
    setVariantForm({
      product_id: '',
      sku_variacao: '',
      cor: '',
      tamanho_label: '',
      medida_text: '',
      espessura_mm: '',
      comprimento_cm: '',
      largura_cm: '',
      preco_custo: ''
    });
    setProductForm({
      nome: '',
      sku: '',
      categoria_id: '',
      marca_id: '',
      unidade_id: ''
    });
    setSelectedProduct(null);
    setQuickAddMode(null);
  };

  const handleProductCreated = async (response) => {
    setShowProductFormComplete(false);
    queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
    queryClient.invalidateQueries({ queryKey: ['products-for-variant'] });

    const newProdId = response?.data?.id || response?.id;
    if (newProdId) {
      try {
        const detailsRes = await inventoryService.getProductDetails(newProdId);
        const details = detailsRes?.data || detailsRes;
        if (details && Array.isArray(details.variantes) && details.variantes.length > 0) {
          if (details.variantes.length === 1) {
            const v = details.variantes[0];
            let supplierPrice = null;
            if (supplierId && Array.isArray(v.fornecedores_precos) && v.fornecedores_precos.length > 0) {
              supplierPrice = v.fornecedores_precos.find(sp => String(sp.fornecedor_id) === String(supplierId));
            }
            if (!supplierPrice && Array.isArray(v.fornecedores_precos) && v.fornecedores_precos.length > 0) {
              supplierPrice = v.fornecedores_precos.find(sp => sp.is_preferencial) || v.fornecedores_precos[0];
            }
            const variantItem = {
              id: v.id,
              variant_id: v.id,
              product_id: details.id,
              product_name: details.nome,
              nome: details.nome,
              variant_sku: v.sku_variacao || details.sku,
              sku_variacao: v.sku_variacao,
              variant_cor: v.cor,
              variant_medida: v.medida_text,
              variant_tamanho: v.tamanho_label,
              espessura_mm: v.espessura_mm,
              preco_custo: supplierPrice ? parseFloat(supplierPrice.preco_custo || 0) : parseFloat(v.preco_custo || 0),
              supplier_price_id: supplierPrice ? supplierPrice.id : null,
              supplier_id: supplierPrice ? supplierPrice.fornecedor_id : (supplierId || null),
              fornecedor_id: supplierPrice ? supplierPrice.fornecedor_id : (supplierId || null),
              supplier_name: supplierPrice ? supplierPrice.fornecedor_nome : null,
              fornecedor_nome: supplierPrice ? supplierPrice.fornecedor_nome : null,
              supplier_ref: supplierPrice ? supplierPrice.referencia_fornecedor : null,
              referencia_fornecedor: supplierPrice ? supplierPrice.referencia_fornecedor : null,
              is_preferencial: supplierPrice ? supplierPrice.is_preferencial : false,
              estoque_disponivel: v.estoque_disponivel ?? v.estoque_total ?? 0,
              estoque_total: v.estoque_total ?? 0
            };
            handleSelectProduct(variantItem);
            return;
          } else {
            setSearchTerm(details.nome);
            handleSearch(details.nome);
            return;
          }
        }
      } catch (err) {
        console.error('Erro ao buscar detalhes do produto recém criado:', err);
      }
    }

    const prodName = response?.data?.nome || response?.nome;
    if (prodName) {
      setSearchTerm(prodName);
      handleSearch(prodName);
    }
  };

  const handleSearch = useCallback(async (term) => {
    setSearchTerm(term);

    if (term.length < 2) {
      setSearchResults([]);
      setShowAutocomplete(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await purchasesService.searchVariants(term, supplierId);
      if (response?.data?.data) {
        setSearchResults(response.data.data.slice(0, 30));
        setShowAutocomplete(true);
      } else if (response?.data) {
        setSearchResults(Array.isArray(response.data) ? response.data.slice(0, 30) : []);
        setShowAutocomplete(true);
      }
    } catch (error) {
      console.error('Erro ao buscar variantes:', error);
    } finally {
      setIsSearching(false);
    }
  }, [supplierId]);

  const handleSelectProduct = (variant) => {
    const newItem = {
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
      variant_id: variant.variant_id || variant.id,
      product_id: variant.product_id,
      quantity: 1,
      unit_price: parseFloat(variant.preco_custo || 0),
      supplier_price_id: variant.supplier_price_id || null,
      supplier_id: variant.fornecedor_id || null,
      supplier_name: variant.fornecedor_nome || null,
      supplier_ref: variant.referencia_fornecedor || null,
      variant: variant
    };

    // Permitir adicionar variantes com fornecedores diferentes ou alertar se a mesma oferta já estiver na lista
    const existingIndex = items.findIndex(item =>
      item.variant_id === newItem.variant_id &&
      String(item.supplier_id || '') === String(newItem.supplier_id || '')
    );
    if (existingIndex >= 0) {
      toast.error('Este item com este mesmo fornecedor já foi adicionado');
      return;
    }

    const newItems = [...items, newItem];
    setItems(newItems);
    validateItems(newItems);
    setSearchTerm('');
    setSearchResults([]);
    setShowAutocomplete(false);
  };

  const handleQuantityChange = (index, newQuantity) => {
    const qty = parseFloat(newQuantity) || 0;
    if (qty <= 0) return;

    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      quantity: qty
    };
    setItems(newItems);
    validateItems(newItems);
  };

  const handlePriceChange = (index, newPrice) => {
    const price = parseFloat(newPrice) || 0;
    if (price < 0) return;

    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      unit_price: price
    };
    setItems(newItems);
    validateItems(newItems);
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    validateItems(newItems);
  };

  const handleQuickAddVariant = () => {
    if (!selectedProduct && quickAddMode === 'variant') {
      toast.error('Selecione um produto primeiro');
      return;
    }

    const variantData = {
      sku_variacao: variantForm.sku_variacao || `VAR-${Date.now()}`,
      cor: variantForm.cor || null,
      tamanho_label: variantForm.tamanho_label || null,
      medida_text: variantForm.medida_text || null,
      espessura_mm: variantForm.espessura_mm ? parseFloat(variantForm.espessura_mm) : null,
      comprimento_cm: variantForm.comprimento_cm ? parseFloat(variantForm.comprimento_cm) : null,
      largura_cm: variantForm.largura_cm ? parseFloat(variantForm.largura_cm) : null,
      preco_custo: variantForm.preco_custo ? parseFloat(variantForm.preco_custo) : 0
    };

    createVariantMutation.mutate({
      productId: selectedProduct?.id || variantForm.product_id,
      data: variantData
    });
  };

  const handleQuickAddProduct = () => {
    if (!productForm.nome) {
      toast.error('Nome do produto é obrigatório');
      return;
    }

    const productData = {
      nome: productForm.nome,
      sku: productForm.sku || `PROD-${Date.now()}`,
      categoria_id: productForm.categoria_id ? parseInt(productForm.categoria_id) : null,
      marca_id: productForm.marca_id ? parseInt(productForm.marca_id) : null,
      unidade_id: productForm.unidade_id ? parseInt(productForm.unidade_id) : null,
      ativo: true
    };

    createProductMutation.mutate(productData);
  };

  const calculateDiscountDetails = (itemsList, mode = discountMode, inputVal = discountInput) => {
    const subtotal = (itemsList || []).reduce((sum, item) => {
      return sum + (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0));
    }, 0);

    let adjustment = 0; // Negativo = Desconto (-), Positivo = Acréscimo / Frete (+)
    const num = parseFloat(inputVal) || 0;

    if (num > 0) {
      if (mode === 'target') {
        // Modo inteligente: Valor Final Faturado da Nota
        // Se maior que o subtotal: gera acréscimo (+). Se menor: gera desconto (-).
        adjustment = subtotal > 0 ? (num - subtotal) : 0;
      } else if (mode === 'discount_val') {
        adjustment = -Math.min(subtotal, num);
      } else if (mode === 'addition_val') {
        adjustment = num;
      } else if (mode === 'percent') {
        adjustment = -Math.min(subtotal, (subtotal * num) / 100);
      }
    }

    adjustment = Math.round(adjustment * 100) / 100;
    const finalTotal = (mode === 'target' && num > 0)
      ? num 
      : Math.max(0, Math.round((subtotal + adjustment) * 100) / 100);

    const isDiscount = adjustment < 0;
    const isAddition = adjustment > 0;
    const adjustmentAmount = Math.abs(adjustment);

    return { 
      subtotal, 
      adjustment, 
      isDiscount, 
      isAddition, 
      adjustmentAmount, 
      finalTotal 
    };
  };

  const validateItems = (itemsList, mode = discountMode, inputVal = discountInput) => {
    const itemCount = itemsList.length;
    const { subtotal, adjustment, finalTotal } = calculateDiscountDetails(itemsList, mode, inputVal);

    const isValid = itemCount > 0 && itemsList.every(item =>
      item.variant_id && item.quantity > 0 && item.unit_price >= 0
    );

    if (onValidationChange) {
      // adjustment negativo = desconto, adjustment positivo = acréscimo
      onValidationChange(isValid, itemCount, finalTotal, adjustment, subtotal);
    }
  };

  const handleDiscountInputChange = (val) => {
    setDiscountInput(val);
    validateItems(items, discountMode, val);
  };

  const handleDiscountModeChange = (newMode) => {
    const { subtotal, adjustment, finalTotal } = calculateDiscountDetails(items, discountMode, discountInput);
    setDiscountMode(newMode);
    let newInput = '';
    if (newMode === 'target') {
      newInput = finalTotal > 0 ? finalTotal.toString() : '';
    } else if (newMode === 'discount_val') {
      newInput = adjustment < 0 ? Math.abs(adjustment).toString() : '';
    } else if (newMode === 'addition_val') {
      newInput = adjustment > 0 ? adjustment.toString() : '';
    } else if (newMode === 'percent') {
      newInput = (subtotal > 0 && adjustment < 0) ? ((Math.abs(adjustment) / subtotal) * 100).toFixed(2) : '';
    }
    setDiscountInput(newInput);
    validateItems(items, newMode, newInput);
  };

  const handleClearDiscount = () => {
    setDiscountInput('');
    validateItems(items, discountMode, '');
  };

  const getItemTotal = (item) => {
    return (parseFloat(item.quantity || 0) * parseFloat(item.unit_price || 0));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const { 
    subtotal: currentSubtotal, 
    adjustment: currentAdjustment,
    isDiscount: currentIsDiscount,
    isAddition: currentIsAddition,
    adjustmentAmount: currentAdjustmentAmount,
    finalTotal: currentFinalTotal 
  } = calculateDiscountDetails(items);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Itens da Despesa
        </label>
        {items.length > 0 && (
          <span className="text-xs text-gray-500">
            {items.length} item(ns)
          </span>
        )}
      </div>

      {!disabled && (
        <div className="space-y-2">
          <div className="relative">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowAutocomplete(true)}
                  placeholder="Buscar produto ou variante..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  disabled={disabled}
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-200 border-t-purple-600"></div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowProductFormComplete(true)}
                className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
                title="Cadastrar Novo Produto Completo"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Novo</span>
              </button>
            </div>

            {showAutocomplete && searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto divide-y divide-gray-100">
                {searchResults.map((variant, sIdx) => {
                  const itemKey = `${variant.variant_id || variant.id}_${variant.supplier_price_id || variant.fornecedor_id || sIdx}`;
                  return (
                    <button
                      key={itemKey}
                      type="button"
                      onClick={() => handleSelectProduct(variant)}
                      className="w-full px-4 py-3 text-left hover:bg-purple-50/50 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 truncate">
                            {variant.product_name || variant.nome || 'Produto sem nome'}
                          </p>

                          <p className="text-xs text-gray-500 mt-0.5">
                            <span className="font-mono font-medium text-gray-700">
                              SKU: {variant.variant_sku || variant.sku_variacao || variant.sku || 'N/A'}
                            </span>
                            {variant.variant_cor && <span> • Cor: {variant.variant_cor}</span>}
                            {variant.variant_medida && <span> • Medida: {variant.variant_medida}</span>}
                            {variant.variant_tamanho && !variant.variant_medida && <span> • {variant.variant_tamanho}</span>}
                            {variant.espessura_mm && <span> • {Math.round(parseFloat(variant.espessura_mm))}mm</span>}
                          </p>

                          {/* Linha de Fornecedor e Referência */}
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            {variant.fornecedor_nome ? (
                              <>
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded border border-purple-200 shadow-2xs">
                                  <Building2 className="w-3 h-3 text-purple-600" />
                                  <span>{variant.fornecedor_nome}</span>
                                </span>
                                {variant.is_preferencial && (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200 shadow-2xs">
                                    <Star className="w-3 h-3 fill-current text-amber-500" />
                                    <span>Preferencial</span>
                                  </span>
                                )}
                                {variant.referencia_fornecedor && (
                                  <span className="text-[10px] text-gray-600 font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                                    Ref: {variant.referencia_fornecedor}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                                <Box className="w-3 h-3 text-gray-400" />
                                <span>Custo base geral (sem fornecedor vinculado)</span>
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="text-right flex-shrink-0">
                          <p className="text-sm font-extrabold text-emerald-700">
                            {formatCurrency(variant.preco_custo)}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Estoque: <strong className="text-gray-800 font-semibold">{variant.estoque_disponivel ?? variant.estoque_total ?? 0}</strong>
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {showAutocomplete && searchResults.length === 0 && searchTerm.length >= 2 && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-sm text-gray-600 text-center mb-3">Nenhum produto encontrado</p>
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  onClick={() => { setQuickAddMode('variant'); setShowQuickAdd(true); }}
                  className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Layers className="w-4 h-4" />
                  Criar Variante
                </button>
                <button
                  type="button"
                  onClick={() => setShowProductFormComplete(true)}
                  className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-2"
                >
                  <Box className="w-4 h-4" />
                  Criar Produto
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Add Modal */}
      {showQuickAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {quickAddMode === 'product' ? 'Criar Produto Completo' : 'Criar Nova Variante'}
              </h3>
              <button
                onClick={() => { setShowQuickAdd(false); resetQuickAddForms(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {quickAddMode === 'product' && !selectedProduct ? (
                <>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Modo:</strong> Criar novo produto com variante
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nome do Produto *
                      </label>
                      <input
                        type="text"
                        value={productForm.nome}
                        onChange={(e) => setProductForm(prev => ({ ...prev, nome: e.target.value }))}
                        placeholder="Ex: MDF MDP"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                      <input
                        type="text"
                        value={productForm.sku}
                        onChange={(e) => setProductForm(prev => ({ ...prev, sku: e.target.value }))}
                        placeholder="Código interno"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
                      <select
                        value={productForm.categoria_id}
                        onChange={(e) => setProductForm(prev => ({ ...prev, categoria_id: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecione</option>
                        {categoriesData?.data?.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.nome || cat.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                      <select
                        value={productForm.marca_id}
                        onChange={(e) => setProductForm(prev => ({ ...prev, marca_id: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecione</option>
                        {brandsData?.data?.map(brand => (
                          <option key={brand.id} value={brand.id}>{brand.nome || brand.name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                      <select
                        value={productForm.unidade_id}
                        onChange={(e) => setProductForm(prev => ({ ...prev, unidade_id: e.target.value }))}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecione</option>
                        {unitsData?.data?.map(unit => (
                          <option key={unit.id} value={unit.id}>{unit.nome || unit.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-3">Dados da Variante</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">SKU Variação</label>
                        <input
                          type="text"
                          value={variantForm.sku_variacao}
                          onChange={(e) => setVariantForm(prev => ({ ...prev, sku_variacao: e.target.value }))}
                          placeholder="VAR-001"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Cor</label>
                        <input
                          type="text"
                          value={variantForm.cor}
                          onChange={(e) => setVariantForm(prev => ({ ...prev, cor: e.target.value }))}
                          placeholder="Branco"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Tamanho</label>
                        <input
                          type="text"
                          value={variantForm.tamanho_label}
                          onChange={(e) => setVariantForm(prev => ({ ...prev, tamanho_label: e.target.value }))}
                          placeholder="15mm"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Preço Custo</label>
                        <input
                          type="number"
                          step="0.01"
                          value={variantForm.preco_custo}
                          onChange={(e) => setVariantForm(prev => ({ ...prev, preco_custo: e.target.value }))}
                          placeholder="0.00"
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : quickAddMode === 'variant' && (
                <>
                  {!selectedProduct ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Selecione o Produto</label>
                      <select
                        value={variantForm.product_id}
                        onChange={(e) => {
                          const product = productsData?.data?.find(p => p.id === parseInt(e.target.value));
                          setSelectedProduct(product);
                          setVariantForm(prev => ({ ...prev, product_id: e.target.value }));
                        }}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      >
                        <option value="">Selecione um produto</option>
                        {productsData?.data?.map(product => (
                          <option key={product.id} value={product.id}>{product.nome || product.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>Produto:</strong> {selectedProduct.nome || selectedProduct.name}
                      </p>
                      <button
                        type="button"
                        onClick={() => { setSelectedProduct(null); setVariantForm(prev => ({ ...prev, product_id: '' })); }}
                        className="text-xs text-blue-600 hover:underline mt-1"
                      >
                        Trocar produto
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">SKU Variação *</label>
                      <input
                        type="text"
                        value={variantForm.sku_variacao}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, sku_variacao: e.target.value }))}
                        placeholder="VAR-001"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Cor</label>
                      <input
                        type="text"
                        value={variantForm.cor}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, cor: e.target.value }))}
                        placeholder="Branco"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Tamanho/Rótulo</label>
                      <input
                        type="text"
                        value={variantForm.tamanho_label}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, tamanho_label: e.target.value }))}
                        placeholder="15mm"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Medida Texto</label>
                      <input
                        type="text"
                        value={variantForm.medida_text}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, medida_text: e.target.value }))}
                        placeholder="2750x1850x15mm"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Espessura (mm)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.espessura_mm}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, espessura_mm: e.target.value }))}
                        placeholder="15"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Comprimento (cm)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.comprimento_cm}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, comprimento_cm: e.target.value }))}
                        placeholder="275"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Largura (cm)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.largura_cm}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, largura_cm: e.target.value }))}
                        placeholder="185"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Preço Custo *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={variantForm.preco_custo}
                        onChange={(e) => setVariantForm(prev => ({ ...prev, preco_custo: e.target.value }))}
                        placeholder="0.00"
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
              <button
                type="button"
                onClick={() => { setShowQuickAdd(false); resetQuickAddForms(); }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={quickAddMode === 'product' ? handleQuickAddProduct : handleQuickAddVariant}
                disabled={createProductMutation.isPending || createVariantMutation.isPending}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                {createProductMutation.isPending || createVariantMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Salvando...
                  </>
                ) : (
                  <>Salvar e Adicionar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {items.length > 0 ? (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Produto</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase w-24">Qtd</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-32">Preço Unit.</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase w-28">Total</th>
                {!disabled && <th className="px-3 py-2 w-12"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((item, index) => (
                <tr key={item.id || index} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.variant?.product_name || item.variant?.nome || item.product_name || 'Produto'}
                      </p>
                      <p className="text-xs text-gray-500">
                        SKU: {item.variant?.variant_sku || item.variant?.sku_variacao || item.variant_sku || 'N/A'}
                        {item.variant?.variant_cor && ` • ${item.variant.variant_cor}`}
                        {item.variant?.variant_medida && ` • ${item.variant.variant_medida}`}
                      </p>
                      {(item.supplier_name || item.variant?.fornecedor_nome) && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-purple-900 bg-purple-50 px-2 py-0.5 rounded border border-purple-200">
                            <Building2 className="w-3 h-3 text-purple-600" />
                            <span>{item.supplier_name || item.variant?.fornecedor_nome}</span>
                          </span>
                          {(item.supplier_ref || item.variant?.referencia_fornecedor) && (
                            <span className="text-[11px] text-gray-600 font-mono bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                              Ref: {item.supplier_ref || item.variant?.referencia_fornecedor}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {disabled ? (
                      <span className="text-sm text-gray-900 text-center block">{item.quantity}</span>
                    ) : (
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-center focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {disabled ? (
                      <span className="text-sm text-gray-900 text-right block">{formatCurrency(item.unit_price)}</span>
                    ) : (
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs">R$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => handlePriceChange(index, e.target.value)}
                          className="w-full pl-7 pr-2 py-1 border border-gray-300 rounded text-sm text-right focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <span className="text-sm font-semibold text-green-600">
                      {formatCurrency(getItemTotal(item))}
                    </span>
                  </td>
                  {!disabled && (
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 divide-y divide-gray-200">
              {/* Subtotal dos Itens */}
              <tr>
                <td colSpan="3" className="px-3 py-2 text-right">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Subtotal dos Itens:</span>
                </td>
                <td className="px-3 py-2 text-right">
                  <span className="text-sm font-bold text-gray-900">
                    {formatCurrency(currentSubtotal)}
                  </span>
                </td>
                {disabled && <td></td>}
              </tr>

              {/* Linha de Ajuste da Nota (Desconto ou Acréscimo / Frete) */}
              {!disabled ? (
                <tr className={currentIsAddition ? 'bg-blue-50/70' : 'bg-amber-50/70'}>
                  <td colSpan="3" className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      <span className={`text-xs font-bold flex items-center gap-1 ${currentIsAddition ? 'text-blue-900' : 'text-amber-900'}`}>
                        {currentIsAddition ? '🚚 Frete / Acréscimo NF:' : '🏷️ Ajuste / Desconto NF:'}
                      </span>
                      <div className="inline-flex rounded-lg bg-white border border-gray-300 p-0.5 shadow-2xs">
                        <button
                          type="button"
                          onClick={() => handleDiscountModeChange('target')}
                          className={`px-2 py-0.5 rounded text-[11px] font-semibold transition-all ${
                            discountMode === 'target'
                              ? 'bg-purple-600 text-white shadow-xs'
                              : 'text-gray-600 hover:text-purple-700'
                          }`}
                          title="Digite o valor exato final da nota para calcular desconto ou acréscimo automaticamente"
                        >
                          Valor Final da NF
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDiscountModeChange('discount_val')}
                          className={`px-2 py-0.5 rounded text-[11px] font-semibold transition-all ${
                            discountMode === 'discount_val'
                              ? 'bg-amber-500 text-white shadow-xs'
                              : 'text-gray-600 hover:text-amber-800'
                          }`}
                        >
                          - Desconto (R$)
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDiscountModeChange('addition_val')}
                          className={`px-2 py-0.5 rounded text-[11px] font-semibold transition-all ${
                            discountMode === 'addition_val'
                              ? 'bg-blue-600 text-white shadow-xs'
                              : 'text-gray-600 hover:text-blue-800'
                          }`}
                          title="Adicionar frete, impostos (ST/IPI) ou acréscimo financeiro"
                        >
                          + Acréscimo / Frete (R$)
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDiscountModeChange('percent')}
                          className={`px-2 py-0.5 rounded text-[11px] font-semibold transition-all ${
                            discountMode === 'percent'
                              ? 'bg-amber-500 text-white shadow-xs'
                              : 'text-gray-600 hover:text-amber-800'
                          }`}
                        >
                          % Desconto
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <span className="text-xs font-bold text-gray-600">
                        {discountMode === 'percent' ? '%' : 'R$'}
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={discountInput}
                        onChange={(e) => handleDiscountInputChange(e.target.value)}
                        placeholder={
                          discountMode === 'target' 
                            ? (currentSubtotal > 0 ? currentSubtotal.toFixed(2) : '1000.00')
                            : (discountMode === 'percent' ? '5.0' : '0.00')
                        }
                        className={`w-24 sm:w-28 px-2 py-1 text-right text-xs font-bold border rounded-lg bg-white focus:ring-2 shadow-2xs ${
                          currentIsAddition 
                            ? 'border-blue-300 text-blue-900 focus:ring-blue-500 focus:border-blue-500' 
                            : 'border-amber-300 text-amber-900 focus:ring-amber-500 focus:border-amber-500'
                        }`}
                      />
                      {currentAdjustmentAmount > 0 && (
                        <button
                          type="button"
                          onClick={handleClearDiscount}
                          className="text-gray-400 hover:text-red-600 p-0.5"
                          title="Remover ajuste"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {currentAdjustmentAmount > 0 && (
                      <p className={`text-[10px] font-bold mt-0.5 ${currentIsAddition ? 'text-blue-700' : 'text-emerald-700'}`}>
                        {currentIsAddition ? `+ ${formatCurrency(currentAdjustmentAmount)} (acréscimo)` : `- ${formatCurrency(currentAdjustmentAmount)} (desconto)`}
                      </p>
                    )}
                  </td>
                  {!disabled && <td></td>}
                </tr>
              ) : (
                currentAdjustmentAmount > 0 && (
                  <tr className={currentIsAddition ? 'bg-blue-50/70' : 'bg-amber-50/70'}>
                    <td colSpan="3" className="px-3 py-2 text-right">
                      <span className={`text-xs font-bold ${currentIsAddition ? 'text-blue-900' : 'text-amber-900'}`}>
                        {currentIsAddition ? 'Frete / Acréscimo NF:' : 'Desconto do Fornecedor:'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className={`text-sm font-bold ${currentIsAddition ? 'text-blue-700' : 'text-emerald-700'}`}>
                        {currentIsAddition ? `+ ${formatCurrency(currentAdjustmentAmount)}` : `- ${formatCurrency(currentAdjustmentAmount)}`}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                )
              )}

              {/* Total Final do Pedido / Nota Fiscal */}
              <tr className="bg-purple-50/80">
                <td colSpan="3" className="px-3 py-2.5 text-right">
                  <span className="text-sm font-bold text-purple-950">Valor Final do Pedido (Total NF):</span>
                </td>
                <td className="px-3 py-2.5 text-right">
                  <span className="text-base sm:text-lg font-black text-purple-900">
                    {formatCurrency(currentFinalTotal)}
                  </span>
                </td>
                {!disabled && <td></td>}
              </tr>
            </tfoot>
          </table>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
          <Package className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 mb-1">Nenhum item adicionado</p>
          <p className="text-xs text-gray-400">Busque e adicione produtos acima ou crie novos</p>
        </div>
      )}

      {errors?.items && (
        <div className="flex items-center gap-1 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{errors.items}</span>
        </div>
      )}

      {/* Modal Oficial de Criação Completa de Produto (Estoque) */}
      {showProductFormComplete && (
        <ProductFormComplete
          mode="create"
          onClose={() => setShowProductFormComplete(false)}
          onSuccess={handleProductCreated}
        />
      )}
    </div>
  );
};

export default PurchaseItemsGrid;
